import React from 'react';
import '../App.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-container">
      <div className="privacy-content">
        {/* Header */}
        <div className="privacy-header">
          <h1>Privacy Policy</h1>
          <div className="company-info">
            <h2>Attenzef</h2>
            <p>A Product of ZEFTACK</p>
            <p className="effective-date">Effective Date: August 28, 2025</p>
            <p className="last-updated">Last Updated: August 28, 2025</p>
          </div>
        </div>

        {/* Introduction */}
        <section className="privacy-section">
          <h3>1. Introduction</h3>
          <p>
            Welcome to Attenzef, an educational attendance management system developed by ZEFTACK. 
            This Privacy Policy explains how we collect, use, protect, and share information when you 
            use our mobile application and web platform. We are committed to protecting your privacy 
            and ensuring the security of your personal information.
          </p>
          <p>
            By using Attenzef, you agree to the collection and use of information in accordance with 
            this Privacy Policy. If you do not agree with our policies and practices, please do not 
            use our services.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="privacy-section">
          <h3>2. Information We Collect</h3>
          
          <h4>2.1 Personal Information</h4>
          <ul>
            <li><strong>Student Information:</strong> Name, student ID, email address, phone number, academic details</li>
            <li><strong>Lecturer Information:</strong> Name, staff ID, email address, phone number, department details</li>
            <li><strong>Administrative Information:</strong> Name, email address, role, institutional details</li>
          </ul>

          <h4>2.2 Location Information</h4>
          <ul>
            <li><strong>GPS Location:</strong> We collect precise location data to verify attendance within designated areas</li>
            <li><strong>Geofencing:</strong> Location data is used to ensure students are physically present at the correct location</li>
            <li><strong>Location History:</strong> We may store location data associated with attendance records for verification purposes</li>
          </ul>

          <h4>2.3 Camera and Device Information</h4>
          <ul>
            <li><strong>Camera Access:</strong> Used for QR code scanning to mark attendance</li>
            <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers</li>
            <li><strong>App Usage Data:</strong> Features used, time spent in app, interaction patterns</li>
          </ul>

          <h4>2.4 Academic Data</h4>
          <ul>
            <li><strong>Attendance Records:</strong> Date, time, location, and method of attendance marking</li>
            <li><strong>Course Information:</strong> Enrolled courses, schedules, academic periods</li>
            <li><strong>Performance Data:</strong> Attendance rates, participation metrics</li>
          </ul>
        </section>

        {/* How We Use Your Information */}
        <section className="privacy-section">
          <h3>3. How We Use Your Information</h3>
          
          <h4>3.1 Primary Purposes</h4>
          <ul>
            <li><strong>Attendance Tracking:</strong> Record and verify student attendance in classes</li>
            <li><strong>Academic Management:</strong> Generate reports, track academic performance</li>
            <li><strong>Authentication:</strong> Verify user identity and prevent unauthorized access</li>
            <li><strong>Communication:</strong> Send notifications about classes, attendance, and system updates</li>
          </ul>

          <h4>3.2 Location Data Usage</h4>
          <ul>
            <li>Verify students are physically present at the correct classroom or campus location</li>
            <li>Prevent fraudulent attendance marking from unauthorized locations</li>
            <li>Generate location-based attendance reports for administrative purposes</li>
            <li>Ensure compliance with institutional attendance policies</li>
          </ul>

          <h4>3.3 Camera Usage</h4>
          <ul>
            <li>Scan QR codes for quick and secure attendance marking</li>
            <li>Verify lecturer-generated attendance sessions</li>
            <li>Enable seamless check-in/check-out processes</li>
          </ul>
        </section>

        {/* Data Security and Protection */}
        <section className="privacy-section">
          <h3>4. Data Security and Protection</h3>
          
          <h4>4.1 Security Measures</h4>
          <ul>
            <li><strong>Encryption:</strong> All data is encrypted in transit and at rest</li>
            <li><strong>Secure Servers:</strong> Data is stored on secure, regularly monitored servers</li>
            <li><strong>Access Controls:</strong> Role-based access ensures users only see relevant data</li>
            <li><strong>Regular Audits:</strong> Security practices are regularly reviewed and updated</li>
          </ul>

          <h4>4.2 Data Retention</h4>
          <ul>
            <li>Attendance records are retained for the duration of academic programs plus 7 years</li>
            <li>Personal information is retained only as long as necessary for educational purposes</li>
            <li>Location data is automatically deleted after 1 year unless required for disputes</li>
            <li>Inactive accounts are anonymized after 2 years of inactivity</li>
          </ul>
        </section>

        {/* Information Sharing */}
        <section className="privacy-section">
          <h3>5. Information Sharing and Disclosure</h3>
          
          <h4>5.1 Educational Institution Sharing</h4>
          <ul>
            <li>Attendance data is shared with authorized academic staff and administrators</li>
            <li>Reports may be provided to academic departments for performance analysis</li>
            <li>Student records may be shared with relevant educational authorities as required</li>
          </ul>

          <h4>5.2 Third-Party Sharing</h4>
          <ul>
            <li><strong>Service Providers:</strong> We may share data with trusted service providers who assist in app functionality</li>
            <li><strong>Legal Requirements:</strong> We may disclose information when required by law or legal process</li>
            <li><strong>Safety Concerns:</strong> Information may be shared to protect the safety of users or others</li>
          </ul>

          <h4>5.3 No Commercial Use</h4>
          <p>
            We do not sell, rent, or trade your personal information to third parties for commercial purposes. 
            Your educational data is used solely for academic and administrative purposes.
          </p>
        </section>

        {/* User Rights and Controls */}
        <section className="privacy-section">
          <h3>6. Your Rights and Controls</h3>
          
          <h4>6.1 Access and Correction</h4>
          <ul>
            <li>View your personal information and attendance records</li>
            <li>Request corrections to inaccurate information</li>
            <li>Download your attendance data in a portable format</li>
          </ul>

          <h4>6.2 Privacy Controls</h4>
          <ul>
            <li><strong>Location Services:</strong> You can disable location services, but this may limit attendance functionality</li>
            <li><strong>Camera Access:</strong> You can revoke camera permissions, but QR scanning will not be available</li>
            <li><strong>Notifications:</strong> You can control notification preferences in app settings</li>
          </ul>

          <h4>6.3 Data Deletion</h4>
          <ul>
            <li>Request deletion of your personal information (subject to educational record requirements)</li>
            <li>Close your account and remove associated data</li>
            <li>Opt-out of certain data collection practices where possible</li>
          </ul>
        </section>

        {/* Children's Privacy */}
        <section className="privacy-section">
          <h3>7. Children's Privacy (Under 18)</h3>
          <p>
            Attenzef may be used by students under 18 years of age as part of their educational program. 
            We take special care to protect the privacy of minors:
          </p>
          <ul>
            <li>We collect only the minimum information necessary for educational purposes</li>
            <li>Parental consent may be required for certain data collection practices</li>
            <li>Enhanced security measures protect minor students' information</li>
            <li>Educational institutions are responsible for ensuring appropriate consent is obtained</li>
          </ul>
        </section>

        {/* International Data Transfers */}
        <section className="privacy-section">
          <h3>8. International Data Transfers</h3>
          <p>
            Your information may be transferred to and stored on servers located outside your country. 
            We ensure that all international transfers are conducted in accordance with applicable 
            data protection laws and with appropriate safeguards in place.
          </p>
        </section>

        {/* Changes to Privacy Policy */}
        <section className="privacy-section">
          <h3>9. Changes to This Privacy Policy</h3>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices 
            or applicable laws. We will notify you of any material changes by:
          </p>
          <ul>
            <li>Posting the updated policy on our platform</li>
            <li>Sending notification through the app or email</li>
            <li>Requiring acknowledgment of changes before continued use</li>
          </ul>
          <p>
            Your continued use of Attenzef after any changes indicates your acceptance of the 
            updated Privacy Policy.
          </p>
        </section>

        {/* Contact Information */}
        <section className="privacy-section">
          <h3>10. Contact Us</h3>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or 
            how we handle your personal information, please contact us:
          </p>
          
          <div className="contact-info">
            <h4>ZEFTACK Privacy Team</h4>
            <p><strong>Product:</strong> Attenzef</p>
            <p><strong>Email:</strong> info@zeftack.com</p>
            <p><strong>Support Email:</strong> info@zeftack.com</p>
            <p><strong>Data Protection Officer:</strong> info@zeftack.com</p>
            
            <h4>Response Time</h4>
            <p>We will respond to privacy inquiries within 30 days of receipt.</p>
            
            <h4>Compliance</h4>
            <p>
              This Privacy Policy complies with applicable data protection laws including GDPR, 
              CCPA, and local educational privacy regulations.
            </p>
          </div>
        </section>

        {/* Footer */}
        <div className="privacy-footer">
          <p>
            <strong>© 2025 ZEFTACK. All rights reserved.</strong>
          </p>
          <p>
            Attenzef is a trademark of ZEFTACK. This Privacy Policy is part of our commitment 
            to protecting your privacy and ensuring responsible data handling in educational technology.
          </p>
        </div>
      </div>

      {/* Styling */}
      <style jsx>{`
        .privacy-policy-container {
          max-width: 900px;
          margin: 0 auto;
          padding: 20px;
          font-family: 'Arial', sans-serif;
          line-height: 1.6;
          color: #333;
          background-color: #f9f9f9;
          min-height: 100vh;
        }

        .privacy-content {
          background: white;
          padding: 40px;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .privacy-header {
          text-align: center;
          border-bottom: 3px solid #F04915;
          padding-bottom: 30px;
          margin-bottom: 40px;
        }

        .privacy-header h1 {
          font-size: 2.5em;
          color: #131A1A;
          margin-bottom: 10px;
        }

        .company-info h2 {
          font-size: 2em;
          color: #F04915;
          margin: 10px 0;
        }

        .company-info p {
          margin: 5px 0;
          color: #666;
        }

        .effective-date, .last-updated {
          font-weight: bold;
          color: #131A1A;
        }

        .privacy-section {
          margin-bottom: 40px;
        }

        .privacy-section h3 {
          font-size: 1.5em;
          color: #131A1A;
          border-left: 4px solid #F04915;
          padding-left: 15px;
          margin-bottom: 20px;
        }

        .privacy-section h4 {
          font-size: 1.2em;
          color: #F04915;
          margin: 20px 0 10px 0;
        }

        .privacy-section p {
          margin-bottom: 15px;
          text-align: justify;
        }

        .privacy-section ul {
          margin: 15px 0;
          padding-left: 20px;
        }

        .privacy-section li {
          margin-bottom: 8px;
        }

        .privacy-section li strong {
          color: #131A1A;
        }

        .contact-info {
          background: #f5f5f5;
          padding: 20px;
          border-radius: 8px;
          border-left: 4px solid #F04915;
        }

        .contact-info h4 {
          color: #131A1A;
          margin-top: 0;
        }

        .contact-info p {
          margin: 8px 0;
        }

        .privacy-footer {
          text-align: center;
          margin-top: 40px;
          padding-top: 30px;
          border-top: 2px solid #F04915;
          color: #666;
        }

        .privacy-footer p {
          margin: 10px 0;
        }

        @media (max-width: 768px) {
          .privacy-policy-container {
            padding: 10px;
          }

          .privacy-content {
            padding: 20px;
          }

          .privacy-header h1 {
            font-size: 2em;
          }

          .company-info h2 {
            font-size: 1.5em;
          }
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;